<script setup>
// Auditoria → Retenção e vendas: pedidos de cancelamento, retidos, cancelados
// (confirmados no SGP), contratações, motivos, ofertas que funcionam e quem
// reteve, a partir das análises dos atendimentos.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createGestaoApi, quando } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  // O período escolhido no topo da Auditoria.
  dias: { type: Number, default: 30 },
});

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const dias = computed(() => props.dias);
const dados = ref(null);
const carregando = ref(true);
const erro = ref(false);

const carrega = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await api.value.retencao(dias.value);
    dados.value = data;
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};
watch(dias, carrega);
onMounted(carrega);

// As classes ficam aqui, num .vue: o Tailwind não lê os .js desta pasta.
const COR_RESULTADO = {
  cancelou: 'text-n-ruby-11 border-n-ruby-6',
  retido: 'text-n-teal-11 border-n-teal-6',
  em_negociacao: 'text-n-amber-11 border-n-amber-6',
  contratou: 'text-n-blue-11 border-n-blue-6',
  upgrade: 'text-n-blue-11 border-n-blue-6',
  downgrade: 'text-n-slate-11 border-n-weak',
  nenhum: 'text-n-slate-11 border-n-weak',
};
const COR_SGP = {
  cancelado: 'text-n-teal-11',
  ativo: 'text-n-amber-11',
  erro: 'text-n-ruby-11',
};

const numeros = computed(() => dados.value?.numeros || {});
const maiorMotivo = computed(() =>
  Math.max(1, ...(dados.value?.motivos || []).map(motivo => motivo.total))
);
const maiorContratacao = computed(() =>
  Math.max(1, ...(dados.value?.contratacoes || []).map(linha => linha.total))
);
const taxa = (retidos, total) =>
  total ? `${Math.round((100 * retidos) / total)}%` : '—';
// fimExclusivo: o fim do relatório é a meia-noite do dia seguinte.
const data = (iso, fimExclusivo = false) =>
  new Date(new Date(iso).getTime() - (fimExclusivo ? 1000 : 0)).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
const linkDaConversa = id =>
  frontendURL(`accounts/${props.accountId}/conversations/${id}`);
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="carregando && !dados" class="flex justify-center py-10"><Spinner /></div>
    <div v-else-if="erro && !dados" class="flex items-center gap-3 text-sm text-n-ruby-11">
      {{ t('NEUXS_GESTAO.ERRO') }}
      <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.TENTAR')" @click="carrega" />
    </div>

    <template v-else-if="dados">
      <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.PEDIDOS') }}</span>
          <strong class="block text-heading-1 text-n-slate-12 tabular-nums">{{ numeros.pedidos }}</strong>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.RETIDOS') }}</span>
          <strong class="block text-heading-1 text-n-teal-11 tabular-nums">{{ numeros.retidos }}</strong>
          <span v-if="numeros.taxa_de_retencao !== null" class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.RETENCAO.TAXA', { n: numeros.taxa_de_retencao }) }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.CANCELADOS') }}</span>
          <strong class="block text-heading-1 text-n-ruby-11 tabular-nums">{{ numeros.cancelados }}</strong>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.RETENCAO.CONFIRMADOS', { n: numeros.confirmados_no_sgp }) }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.CONTRATACOES') }}</span>
          <strong class="block text-heading-1 text-n-blue-11 tabular-nums">{{ numeros.contratacoes }}</strong>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.RETENCAO.UPGRADES', { n: numeros.upgrades }) }}
          </span>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.SALDO') }}</span>
          <strong
            class="block text-heading-1 tabular-nums"
            :class="numeros.saldo < 0 ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ numeros.saldo > 0 ? `+${numeros.saldo}` : numeros.saldo }}
          </strong>
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.SALDO_DICA') }}</span>
        </div>
      </div>

      <section v-if="dados.leitura" class="flex flex-col gap-2 p-4 border rounded-xl border-n-blue-6 bg-n-alpha-1">
        <h2 class="flex flex-wrap items-baseline gap-2 m-0 text-heading-3 text-n-slate-12">
          {{ t('NEUXS_GESTAO.RETENCAO.LEITURA') }}
          <span class="text-xs font-normal text-n-slate-11">
            {{ t('NEUXS_GESTAO.RETENCAO.LEITURA_DICA', { de: data(dados.leitura.inicio), ate: data(dados.leitura.fim, true) }) }}
          </span>
        </h2>
        <ul class="flex flex-col gap-2 m-0 text-sm ps-5 text-n-slate-12 max-w-4xl">
          <li v-for="(item, i) in dados.leitura.itens" :key="i">
            {{ item.texto }}
            <a
              v-for="id in item.conversas"
              :key="id"
              :href="linkDaConversa(id)"
              target="_blank"
              rel="noopener"
              class="text-xs ms-1 text-n-blue-11"
            >#{{ id }}</a>
          </li>
        </ul>
      </section>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RETENCAO.MOTIVOS') }}</h2>
          <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RETENCAO.MOTIVOS_DICA') }}</p>
          <p v-if="!dados.motivos.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <div
            v-for="motivo in dados.motivos"
            :key="motivo.texto"
            class="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 text-sm"
          >
            <span class="truncate text-n-slate-12" :title="motivo.texto">{{ motivo.texto }}</span>
            <span class="flex h-2.5 overflow-hidden rounded bg-n-alpha-2">
              <span class="bg-n-teal-9" :style="{ width: `${(100 * motivo.retidos) / maiorMotivo}%` }" />
              <span
                class="bg-n-ruby-9 opacity-70"
                :style="{ width: `${(100 * (motivo.total - motivo.retidos)) / maiorMotivo}%` }"
              />
            </span>
            <span class="text-xs tabular-nums text-n-slate-11">{{ motivo.retidos }} / {{ motivo.total }}</span>
          </div>
        </section>

        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RETENCAO.OFERTAS') }}</h2>
          <p v-if="!dados.ofertas.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-xs text-left text-n-slate-11">
                <th class="py-1 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.OFERTA') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.OFERTADA') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.RETIDOS') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.TAXA_CURTA') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="oferta in dados.ofertas" :key="oferta.texto">
                <td class="py-1.5 text-n-slate-12">{{ oferta.texto }}</td>
                <td class="py-1.5 text-end tabular-nums">{{ oferta.total }}</td>
                <td class="py-1.5 text-end tabular-nums">{{ oferta.retidos }}</td>
                <td class="py-1.5 text-end tabular-nums text-n-slate-12">{{ taxa(oferta.retidos, oferta.total) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RETENCAO.POR_QUEM') }}</h2>
          <p v-if="!dados.por_quem.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-xs text-left text-n-slate-11">
                <th class="py-1 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.QUEM') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.PEDIDOS') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.RETIDOS') }}</th>
                <th class="py-1 font-normal text-end">{{ t('NEUXS_GESTAO.RETENCAO.TAXA_CURTA') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="linha in dados.por_quem" :key="linha.nome">
                <td class="py-1.5 text-n-slate-12">{{ linha.nome }}</td>
                <td class="py-1.5 text-end tabular-nums">{{ linha.pedidos }}</td>
                <td class="py-1.5 text-end tabular-nums">{{ linha.retidos }}</td>
                <td class="py-1.5 text-end tabular-nums text-n-slate-12">{{ taxa(linha.retidos, linha.pedidos) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RETENCAO.CONTRATACOES_ORIGEM') }}</h2>
          <p v-if="!dados.contratacoes.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <div
            v-for="linha in dados.contratacoes"
            :key="`${linha.resultado}-${linha.origem}`"
            class="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 text-sm"
          >
            <span class="text-n-slate-12">
              {{ t(`NEUXS_GESTAO.DETALHE.RESULTADOS.${linha.resultado}`) }} ·
              {{ t(`NEUXS_GESTAO.RETENCAO.ORIGEM.${linha.origem}`) }}
            </span>
            <span class="h-2.5 overflow-hidden rounded bg-n-alpha-2">
              <span class="block h-full bg-n-blue-9" :style="{ width: `${(100 * linha.total) / maiorContratacao}%` }" />
            </span>
            <span class="text-xs tabular-nums text-n-slate-11">{{ linha.total }}</span>
          </div>
        </section>
      </div>

      <section class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RETENCAO.LISTA') }}</h2>
        <p v-if="!dados.analises.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-left text-n-slate-11">
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.QUANDO') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.CLIENTE') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.RESULTADO') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.NO_SGP') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.MOTIVO') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.OFERTA') }}</th>
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.ATENDEU') }}</th>
                <th class="py-2 font-normal">{{ t('NEUXS_GESTAO.RETENCAO.RESUMO') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak align-top">
              <tr v-for="analise in dados.analises" :key="analise.id">
                <td class="py-2 pe-3 whitespace-nowrap tabular-nums text-n-slate-11">{{ quando(analise.resolvida_em) }}</td>
                <td class="py-2 pe-3 whitespace-nowrap">
                  <a :href="linkDaConversa(analise.conversa)" target="_blank" rel="noopener" class="text-n-blue-11">
                    {{ analise.cliente || `#${analise.conversa}` }}
                  </a>
                </td>
                <td class="py-2 pe-3 whitespace-nowrap">
                  <span class="px-2 py-0.5 text-xs border rounded-full" :class="COR_RESULTADO[analise.resultado]">
                    {{ t(`NEUXS_GESTAO.DETALHE.RESULTADOS.${analise.resultado}`) || '—' }}
                  </span>
                </td>
                <td class="py-2 pe-3 text-xs whitespace-nowrap" :class="COR_SGP[analise.sgp_status] || 'text-n-slate-11'" :title="analise.sgp_detalhe">
                  {{ analise.sgp_status ? t(`NEUXS_GESTAO.RETENCAO.SGP.${analise.sgp_status}`) : '—' }}
                </td>
                <td class="py-2 pe-3 text-n-slate-12">{{ analise.motivo || '—' }}</td>
                <td class="py-2 pe-3 text-n-slate-12">{{ analise.oferta || '—' }}</td>
                <td class="py-2 pe-3 whitespace-nowrap text-n-slate-12">{{ analise.atendeu }}</td>
                <td class="py-2 text-n-slate-11 min-w-64">{{ analise.resumo }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>
